import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Typography } from '@mui/material';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import MicIndicator from '../speech/MicIndicator';
import TextElement from '../text/TextElement';
import StartCover from '../reading-areas/StartCover';
import ScoreStars from './ScoreStars';
import { getPreloadedAudio, playFlowChime } from '../../../utils/sound.utils';
import { useTranslation } from 'react-i18next';

/**
 * A glance at the stars before the next screen. The recogniser's stream is
 * reopened per screen and takes about a second to be ready, so the chime is
 * not played here: it plays when the next screen is actually listening, and
 * the reader learns that the chime means go. Starting on the score lost the
 * first word (ברוך) in a third of the blessings 1mishna read in the flow.
 */
const FLOW_ADVANCE_MS = 400;

/**
 * Reading one Siddur screen out loud.
 *
 * Everything that listens, aligns and scores is the same pipeline the books
 * use. This component only starts it, times it, and writes the result back to
 * the units on screen. The text reaches the pipeline through
 * `siddurStore.currentScreenReading`, which is the single seam between the
 * tree and the reader.
 */
const SiddurReadingView = () => {
  const speech = useStoreByName('speechRecognitionStore') as any;
  const siddurStore = useStoreByName('siddurStore') as any;
  const uiStore = useStoreByName('uiStore') as any;
  const { t } = useTranslation();

  const {
    isDone,
    isStarted,
    isPaused,
    isListening,
    startListening,
    doneListening,
    currentSpokenText,
    score,
    clearData,
  } = speech;

  const containerRef = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState(0);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * The reader touched the screen mid-flow. The preference in the menu is
   * untouched: this screen finishes and is scored, and the flow waits here.
   */
  const [flowStopped, setFlowStopped] = useState(false);
  const flowOn = Boolean(uiStore.autoFlowEnabled) && !flowStopped;
  const mountedRef = useRef(true);
  // The advance timer lives in a ref, not in an effect cleanup: the effect
  // that arms it also flips `saved`, and a cleanup tied to that dependency
  // would cancel the timer on the very next render. Cleared on unmount only.
  const advanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      mountedRef.current = false;
      if (advanceRef.current) clearTimeout(advanceRef.current);
    },
    []
  );
  /**
   * Whether this screen has actually been read yet.
   *
   * The speech store is shared and keeps `isDone` and `score` from the last
   * reading until the next one starts. This view is keyed on the screen, so
   * pressing הבא remounts it, and for the 200ms before listening begins the
   * previous screen's finished reading was still sitting there. The save
   * effect fired on the first render and wrote the old score against the new
   * screen's units, and the old stars appeared before a word had been said.
   */
  const [readingBegan, setReadingBegan] = useState(false);

  const reading = siddurStore.currentScreenReading;

  // One reading per screen. Changing screen or mode tears this down and starts
  // over, which is why the screen index is in the key upstream.
  useEffect(() => {
    uiStore.setIsDoneListeningDialogOpen(false);
    clearData();
    setSaved(false);
    setSaveError(null);
    setReadingBegan(false);
    setTime(0);
    const timeout = setTimeout(() => startListening(true), 200);
    return () => {
      clearTimeout(timeout);
      clearData();
      uiStore.setIsDoneListeningDialogOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isListening) return;
    setReadingBegan(true);
    // The flow's chime: the microphone is open, read.
    if (uiStore.autoFlowEnabled && !flowStopped) playFlowChime(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening]);

  useEffect(() => {
    if (!isListening || isPaused) return undefined;
    const interval = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isListening, isPaused]);

  /**
   * Save once, when the pipeline says the reading is finished.
   *
   * The trigger is `isDone`, which the speech controller sets itself, not the
   * shared "done dialog is open" flag. That flag belongs to the book reader's
   * modal, and a screen that does not mount the modal has no business
   * depending on it: if anything else clears it first, the reading is simply
   * lost with nothing on screen to say so.
   *
   * The save is awaited and its failure is shown. A reading that did not save
   * has to look different from one that did, or a student keeps reading into
   * a system that is quietly dropping the work.
   */
  useEffect(() => {
    if (!readingBegan || !isDone || saved || score === undefined) return;
    setSaved(true);
    setSaveError(null);

    const spoken = (currentSpokenText || '').split(' ').filter(Boolean).length;
    siddurStore
      .saveScreenScores({
        executionTime: new Date(time * 1000).toISOString().slice(14, 19),
        wordsPerMinute: time > 0 ? Math.round((spoken / time) * 60) : 0,
      })
      .catch((error: unknown) => {
        console.error('Siddur score was not saved:', error);
        setSaveError('הציון לא נשמר. נסה שוב.');
      });

    // Outside the flow: the same finish sound the book reader plays. Inside
    // it: a short chime, a breath, and on to the next read. Same screen again
    // while reads-per-verse asks for it, then the next screen; at the end of
    // the prayer the flow simply stops. The microphone is not released
    // between screens: the next view starts listening on mount.
    if (!flowOn) {
      try {
        const sound = getPreloadedAudio(score);
        sound.playbackRate = 1.5;
        if (sound.src) sound.play().catch(() => undefined);
      } catch (error) {
        // no sound is not a failed reading
      }
      return;
    }
    advanceRef.current = setTimeout(() => {
      advanceRef.current = null;
      if (!mountedRef.current) return;
      if (siddurStore.readIndex < uiStore.readsPerVerse) {
        siddurStore.readAgain();
      } else if (siddurStore.hasNext) {
        siddurStore.next();
      } else {
        siddurStore.stopReading();
      }
    }, FLOW_ADVANCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingBegan, isDone, score, saved]);

  if (!reading) {
    return (
      <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
        אין כאן טקסט לקריאה
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      onClick={() => { if (uiStore.autoFlowEnabled && !flowStopped) setFlowStopped(true); }}
    >
      {uiStore.autoFlowEnabled && flowStopped && (
        <Typography variant="body2" color="text.secondary" dir="rtl">
          {t('autoFlowStoppedNotice')}
        </Typography>
      )}
      <div className="start-cover-host">
        <TextElement
          mode={siddurStore.readingMode}
          fontClassName="OpenFrankSiddur"
          preventCopyPaste
          textEntity={reading.entity}
          containerRef={containerRef as React.RefObject<HTMLDivElement>}
        />
        <StartCover />
      </div>

      {/* The buttons are not "the screen": pressing סיימתי or הבא must not
          count as the touch that stops the flow. */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        onClick={(event) => event.stopPropagation()}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MicIndicator />
          <Typography variant="body2" color="text.secondary">
            {isListening ? new Date(time * 1000).toISOString().slice(14, 19) : ''}
          </Typography>
        </Box>

        {saved ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {saveError ? (
              <Typography color="error" variant="body2">{saveError}</Typography>
            ) : (
              <ScoreStars percentage={score} />
            )}
            <Button variant="outlined" onClick={() => siddurStore.stopReading()}>סיום</Button>
            {siddurStore.hasNext && (
              <Button
                variant="contained"
                onClick={() => {
                  siddurStore.next();
                  siddurStore.startReading(siddurStore.readingMode);
                }}
              >
                הבא
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => siddurStore.stopReading()}>ביטול</Button>
            <Button
              variant="contained"
              disabled={isDone || !isStarted}
              onClick={() => doneListening()}
            >
              סיימתי
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default observer(SiddurReadingView);
